<script setup lang="ts">
import { ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { taskCategoriesApi, type TaskCategory } from '@/api/task-categories';

interface Props {
  taskCategory: TaskCategory;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const submitButtonDisabled = ref(false);

const undiscardMutation = useMutation({
  mutationFn: () => taskCategoriesApi.update({ id: props.taskCategory.id }),
  onSuccess: () => {
    emit('success');
    emit('close');
  },
  onError: () => {
    submitButtonDisabled.value = false;
  },
});

function handleSubmit() {
  submitButtonDisabled.value = true;
  undiscardMutation.mutate();
}
</script>
<template>
  <v-card class="pa-4">
    <v-card-title>
      Restaurar Categoría "{{ taskCategory.name }}"
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        ¿Está seguro que desea restaurar la categoría "{{ taskCategory.name }}"?
      </p>
      <p class="mb-4 text-body-2 text-medium-emphasis">
        La categoría será restaurada y volverá a estar disponible para su uso.
        Todas las definiciones de tareas asociadas también serán restauradas.
      </p>
      <v-alert
        type="info"
        variant="tonal"
        class="mb-4"
      >
        <strong>Estado actual:</strong> Archivada el {{ taskCategory.humanDiscardedAt }}
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="undiscardMutation.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="success"
        :loading="undiscardMutation.isPending.value"
        :disabled="submitButtonDisabled"
        @click="handleSubmit"
      >
        Restaurar
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="undiscardMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error. Intenta nuevamente.
    </v-alert>
  </v-card>
</template>
